// Dashboard data fetching and caching hook as specified in appmark.txt
import { useState, useEffect, useCallback } from "react";
import { apiService } from "../services/api";
import {
  DashboardOverview,
  WorkflowStatus,
  AgentStatus,
  ApprovalRequest,
  SystemHealth,
} from "../types";
import { useWebSocketMessages } from "./useWebSocket";

interface DashboardData {
  overview: DashboardOverview | null;
  workflows: WorkflowStatus[];
  agents: AgentStatus[];
  approvals: ApprovalRequest[];
  systemHealth: SystemHealth | null;
  loading: boolean;
  error: string | null;
}

export const useDashboardData = (workflowFilter?: string) => {
  const [data, setData] = useState<DashboardData>({
    overview: null,
    workflows: [],
    agents: [],
    approvals: [],
    systemHealth: null,
    loading: true,
    error: null,
  });

  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setData((prev) => ({ ...prev, loading: true, error: null }));

      const [overview, workflows, agents, approvals, systemHealth] =
        await Promise.all([
          apiService.getDashboardOverview(workflowFilter),
          apiService.getActiveWorkflows(workflowFilter),
          apiService.getAgentStatus(),
          apiService.getApprovalQueue(),
          apiService.getSystemHealth(),
        ]);

      setData({
        overview,
        workflows,
        agents,
        approvals,
        systemHealth,
        loading: false,
        error: null,
      });

      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      setData((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : "Failed to fetch data",
      }));
    }
  }, [workflowFilter]);

  // Initial data fetch
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // WebSocket handling moved to Dashboard component to trigger full refresh including refreshTrigger

  // Handle new approval requests via WebSocket
  useWebSocketMessages(
    "approval_request",
    useCallback((newApproval: ApprovalRequest) => {
      setData((prev) => ({
        ...prev,
        approvals: [...prev.approvals, newApproval],
      }));
    }, [])
  );

  // Handle system alerts via WebSocket
  useWebSocketMessages(
    "system_alert",
    useCallback((alert) => {
      console.log("System alert:", alert);
      // Could trigger toast notifications here
    }, [])
  );

  // Refresh data manually
  const refresh = useCallback(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Get specific workflow by ID
  const getWorkflow = useCallback(
    (workflowId: string) => {
      return data.workflows.find((w) => w.id === workflowId);
    },
    [data.workflows]
  );

  // Get agent by name
  const getAgent = useCallback(
    (agentName: string) => {
      return data.agents.find((a) => a.name === agentName);
    },
    [data.agents]
  );

  return {
    ...data,
    lastRefresh,
    refresh,
    getWorkflow,
    getAgent,
  };
};
